import {FULFILLED} from 'redux-promise-middleware';
import {createSelector} from 'reselect';
import {omit} from 'ramda';

import {ApplicationStoreType} from 'reducers';
import {WindowProductType, SimpleProductType} from 'constants/flow/flowTypesProduct';
import {GENDER_USER_CODE} from 'constants/gender';
import {GenderDataType} from 'reducers/gender/genderType';
import {getCurrentGender} from 'selectors/gender/gender';
import {getCategoryItems} from 'selectors/category/category';
import {getParentProduct} from 'selectors/product/product';

import {TryOnFaceType, CollectionType} from '../reducers/collection';
import {TryOnFrameType} from '../reducers/frame';
import {TryOnPhotoType} from '../reducers/photo';
import {TryOnProductType} from '../reducers/product';
import {TryOnSettingsType} from '../reducers/settings';
import {MODULE_KEY} from '../constants/constants';

type SortingItemType = TryOnFaceType & {orderIndex: number};

/**
 * Get "Try On" data
 * @param {ApplicationStoreType} store - Application store
 * @return {object} "Try On"data
 */
export const getTryOn = (store: ApplicationStoreType) => store[MODULE_KEY] || {};

/**
 * Get "Try On" collection data
 * @param {ApplicationStoreType} store - Application store
 * @return {CollectionType} "Try On" collection data
 */
export const getTryOnCollection = createSelector(
    getTryOn,
    (tryOn): CollectionType => tryOn.collection || {},
);

/**
 * Returns true if request for faces tryOn successfully
 * @return {boolean} boolean value
 */
export const isFulfilledTryOnCollection = createSelector(
    getTryOn,
    (tryOn): boolean => tryOn.collection.status === FULFILLED,
);

/**
 * Get faces for "Try On" slider
 * @param {ApplicationStoreType} store - Application store
 * @return {Array<TryOnFaceType>} Faces for "Try On" slider
 */
export const getTryOnFaces = createSelector(
    getTryOnCollection,
    (tryOn: CollectionType): Array<TryOnFaceType> => tryOn.faces || [],
);

/**
 * Get "Try on" frame data
 * @param {ApplicationStoreType} store - Application store
 * @return {TryOnSettingsType} "Try on" frame data
 */
export const getTryOnSettings = createSelector(
    getTryOn,
    (tryOn): TryOnSettingsType => tryOn.settings,
);

/**
 * Get "Try on" product data
 * @param {ApplicationStoreType} store - Application store
 * @return {TryOnProductType} "Try on" product data
 */
export const getTryOnProduct = createSelector(getTryOn, (tryOn): TryOnProductType => tryOn.product);

/**
 * Get try on status which says that is try on view
 * @param {ApplicationStoreType} store - Application store
 * @return {boolean} "is a try on view.
 */
export const isTryOnView = createSelector(
    getTryOn,
    (tryOn: {isLoadFromCategory: boolean}): boolean => tryOn.isLoadFromCategory,
);

/**
 * Get status is photo of faces is loaded
 * @param {ApplicationStoreType} store - Application store
 * @return {boolean} is photo loaded
 */
export const isPhotoOfFacesLoaded = createSelector(
    getTryOnCollection,
    (tryOnCollection: CollectionType): boolean => tryOnCollection.status === FULFILLED,
);

/**
 * Get parent product for tryOn ID
 * @param {ApplicationStoreType} store - Application store
 * @return {WindowProductType} Window product
 */
export const getActiveProduct = (store: ApplicationStoreType): WindowProductType => {
    const productId = getTryOnCollection(store).productInfo.id;

    const productList = getCategoryItems(store);
    const windowProduct = getParentProduct(store);

    if (windowProduct && windowProduct.children) {
        const children: Array<SimpleProductType> = Object.values(windowProduct.children);

        if (children.some((child: SimpleProductType): boolean => child.id === productId)) {
            return windowProduct;
        }
    }

    return productList.find((item: WindowProductType): boolean => {
        return item.children.some((child: SimpleProductType): boolean => child.id === productId);
    });
};

/**
 * Get current product for tryOn ID
 * @param {ApplicationStoreType} store - Application store
 * @return {SimpleProductType | undefined} Window product
 */
export const getProduct = (store: ApplicationStoreType): SimpleProductType | undefined => {
    const productId = getTryOnCollection(store).productInfo.id;
    const windowProduct = getTryOnProduct(store).product;

    if (Array.isArray(windowProduct.children)) {
        return windowProduct.children.find(
            (child: SimpleProductType): boolean => child.id === productId,
        );
    }

    return windowProduct.children[productId];
};

/**
 * Get array of colors for active product
 * @param {ApplicationStoreType} store - Application store
 * @return {WindowProductType} Window product
 */
export const getColors = createSelector(
    getTryOnProduct,
    ({product}: {product: WindowProductType}): Array<SimpleProductType> =>
        Array.isArray(product.children) ? product.children : Object.values(product.children),
);

/**
 * Get sorted by gender "Try on" faces
 * @param {ApplicationStoreType} store - Application store
 * @return {Array<TryOnFaceType>} - Sorted by gender "Try on" faces
 */
export const getSortedByGenderFaces = createSelector(
    getTryOnFaces,
    getCurrentGender,
    (faces: Array<TryOnFaceType>, {genderId}: GenderDataType): Array<TryOnFaceType> => {
        const arr: SortingItemType[] = faces.map((item, index) => ({...item, orderIndex: index}));

        arr.sort((itemA: SortingItemType, itemB: SortingItemType): number => {
            const genderA = Number(itemA.gender);
            const genderB = Number(itemB.gender);

            if (genderA === genderB) return itemA.orderIndex - itemB.orderIndex;

            // user photos should displayed first
            if (genderA === GENDER_USER_CODE) return -1;
            if (genderB === GENDER_USER_CODE) return 1;

            // sort photos by passed gender
            if (genderA === genderId && genderB !== genderId) return -1;
            if (genderA !== genderId && genderB === genderId) return 1;

            return itemA.orderIndex - itemB.orderIndex;
        });

        return arr.map((item: SortingItemType): TryOnFaceType => omit(['orderIndex'], item));
    },
);

/**
 * Get data of uploaded photo
 * @param {ApplicationStoreType} store - Application store
 * @return {TryOnPhotoType} Data of uploaded photo
 */
export const getUploadedPhoto = (store: ApplicationStoreType): TryOnPhotoType =>
    store[MODULE_KEY].photo;

/**
 * Get status "faces data is loaded"
 * @param {ApplicationStoreType} store - Application store
 * @return {boolean} - Status "faces is loaded"
 */
export const isLoadedFaces = createSelector(
    getTryOnCollection,
    (tryOn: CollectionType): boolean => tryOn.status === FULFILLED,
);

/**
 * Get "Try on" frame data
 * @param {ApplicationStoreType} store - Application store
 * @return {TryOnFrameType} "Try on" frame data
 */
export const getTryOnFrame = createSelector(getTryOn, (tryOn): TryOnFrameType => tryOn.frame);

/**
 * Get status "frame data is loaded"
 * @param {ApplicationStoreType} store - Application store
 * @return {boolean} - Status "faces is loaded"
 */
export const isLoadedFrame = createSelector(
    getTryOnFrame,
    (frame: TryOnFrameType): boolean => frame.status === FULFILLED,
);

/**
 * Get status "face data is loaded"
 * @param {ApplicationStoreType} store - Application store
 * @return {boolean} - Status "user face is loaded"
 */
export const isUpoloadedFace = createSelector(
    getTryOnSettings,
    (settings: TryOnSettingsType): boolean => settings.status === FULFILLED,
);

/**
 * Get data of current face
 * @param {ApplicationStoreType} store - Application store
 * @return {TryOnFaceType} Data of current face
 */
export const getCurrentFace = createSelector(
    getTryOnFaces,
    (faces: Array<TryOnFaceType>): TryOnFaceType =>
        faces.find((face: TryOnFaceType) => face.is_current) || {
            id: 0,
            url: '',
            is_current: false,
            model: '',
            pupils: {
                right: {
                    x: 0,
                    y: 0,
                },
                left: {
                    x: 0,
                    y: 0,
                },
            },
            is_user_photo: false,
            gender: 0,
            pd: 0,
        },
);
